import React, { useMemo } from "react";
import cx from "classnames";
import range from "lodash/range";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";

import useDomain from "hooks/useDomain";
import { useEventTracker } from "hooks/useEventTracker";
import { Action, Category, Label } from "utils/trackEvent";

import ChevronIcon from "components/Icons/ChevronIcon";

type Props = {
  currentPage: number;
  onNext: () => void;
  onPrev: () => void;
  totalPages: number;
  /* onPageSelect is a custom onClick for the pg numbers when it deviates
   * from ?page=3 query param structure. Add custom rel prev / next values
   * in the site head when using.
   */
  onPageSelect?: (page: number) => void;
  pagesToShow?: number;
  className?: string;
};

enum PaginationState {
  noEllipse = "noEllipse",
  leftEllipse = "leftEllipse",
  rightEllipse = "rightEllipse",
}

const PageNavigation: React.FC<Props> = ({
  currentPage,
  onNext,
  onPrev,
  totalPages,
  onPageSelect,
  pagesToShow = 5,
  className,
}) => {
  const { asPath, push } = useRouter();
  const router = useRouter();
  const domain = useDomain();
  const { publishEvent } = useEventTracker();
  const path = `${domain}${asPath.split("?")[0]}?page=`;
  const prevUrl = `${path}${currentPage - 1}`;
  const nextUrl = `${path}${currentPage + 1}`;

  const currentPagination = useMemo(() => {
    let state: PaginationState;
    const totalPagesInt = Number(totalPages);
    const defaultEndRange = totalPagesInt + 1;
    const ellipse = "...";
    const constructPagination = (pagination: (string | number)[]) => {
      return pagination.map((page) => ({
        page,
        path: `${path}${page}`,
      }));
    };
    if (totalPagesInt <= pagesToShow) {
      state = PaginationState.noEllipse;
    } else if (totalPagesInt - 3 < currentPage) {
      state = PaginationState.leftEllipse;
    } else {
      state = PaginationState.rightEllipse;
    }

    switch (state) {
      case PaginationState.rightEllipse: {
        const startRange =
          currentPage - 1 <= 1
            ? 1
            : currentPage + 1 === defaultEndRange - 3
            ? currentPage - 2
            : currentPage - 1;
        const endRange =
          currentPage === 1
            ? currentPage + 3
            : currentPage + 1 === defaultEndRange - 3
            ? currentPage + 1
            : currentPage + 2;
        return constructPagination([
          ...range(startRange, endRange),
          ellipse,
          defaultEndRange - 1,
        ]);
      }
      case PaginationState.leftEllipse: {
        const startRange =
          currentPage - 1 <= defaultEndRange - 3
            ? currentPage === defaultEndRange - 3
              ? currentPage
              : currentPage - 1
            : currentPage - 2;
        const endRange = defaultEndRange;
        return constructPagination([
          1,
          ellipse,
          ...range(startRange, endRange),
        ]);
      }
      default:
        return constructPagination(range(1, defaultEndRange));
    }
  }, [currentPage, totalPages]);

  if (totalPages < 2) {
    return <></>;
  }

  const onPageClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    page: string | number
  ) => {
    e.preventDefault();
    publishEvent({
      action: Action.click,
      category: Category.pagination,
      label: `${Label.page}#${page}`,
    });
    if (onPageSelect) {
      return onPageSelect(page as number);
    }

    return push(
      {
        query: { ...router.query, page: page },
      },
      undefined,
      { scroll: true, shallow: true }
    );
  };

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    onClick: () => void
  ) => {
    e.preventDefault();
    onClick();
  };

  return (
    <>
      {!!onPageSelect && (
        <Head>
          {currentPage > 1 && <link rel="prev" href={prevUrl} />}
          {currentPage < totalPages && <link rel="next" href={nextUrl} />}
        </Head>
      )}
      <nav
        aria-label="Navigate between pages"
        className={cx(className, "flex items-center justify-center")}
      >
        <div className="flex gap-2">
          {currentPage !== 1 && (
            <Link
              className="self-center p-2"
              href={{
                query: { ...router.query, page: currentPage - 1 },
              }}
              onClick={(e) => handleClick(e, onPrev)}
              aria-label="Previous Page"
            >
              <ChevronIcon
                className="inline-block text-green"
                direction="left"
              />
            </Link>
          )}
          <div
            className={`grid grid-flow-col items-center justify-center text-center`}
            style={{
              gridTemplateColumns: `repeat(${
                currentPagination.length >= pagesToShow
                  ? pagesToShow
                  : currentPagination.length
              }, 40px)`,
            }}
            data-testid="pages-shown"
          >
            {currentPagination.map(({ page, path }) => {
              return page === "..." ? (
                <span
                  key={path}
                  className="rounded-sm h-10 flex items-center justify-center text-baseline no-underline"
                >
                  {page}
                </span>
              ) : (
                <Link
                  href={{
                    query: { ...router.query, page },
                  }}
                  key={path}
                  className={cx(
                    "rounded-sm h-10 flex items-center justify-center",
                    {
                      "bg-light-grey": page === currentPage,
                      "underline text-green": page !== currentPage,
                    }
                  )}
                  onClick={(e) => onPageClick(e, page)}
                >
                  {page}
                </Link>
              );
            })}
          </div>
          {currentPage !== Number(totalPages) && (
            <Link
              className="self-center justify-end p-2"
              href={{
                query: { ...router.query, page: currentPage + 1 },
              }}
              onClick={(e) => handleClick(e, onNext)}
              aria-label="Next Page"
            >
              <ChevronIcon
                className="inline-block text-green"
                direction="right"
              />
            </Link>
          )}
        </div>
      </nav>
    </>
  );
};

export default PageNavigation;
